import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Compass, Home, ChevronLeft, ArrowLeft, Lock, Loader2, Users, UserPlus, Trash2, Download, Key, BookOpen, Plus, BarChart3, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { AuthContext, API } from "@/App";
import { toast } from "sonner";

const TeamPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [library, setLibrary] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [showAddPrompt, setShowAddPrompt] = useState(false);
  const [promptForm, setPromptForm] = useState({ title: "", prompt: "", tool_name: "" });
  const [newApiKey, setNewApiKey] = useState(null);

  const hasAccess = user?.is_admin || user?.subscription_tier === "team" || user?.team_id;

  useEffect(() => { if (hasAccess) fetchAll(); else setLoading(false); }, [hasAccess]);

  const fetchAll = async () => {
    try {
      const [tRes, lRes, uRes] = await Promise.all([
        fetch(`${API}/team`, { credentials: "include" }),
        fetch(`${API}/team/library`, { credentials: "include" }),
        fetch(`${API}/team/usage`, { credentials: "include" }),
      ]);
      if (tRes.ok) setTeam(await tRes.json());
      if (lRes.ok) setLibrary(await lRes.json());
      if (uRes.ok) setUsage(await uRes.json());
    } catch (e) {} finally { setLoading(false); }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const res = await fetch(`${API}/team/invite`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ email: inviteEmail.trim() }),
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message); setInviteEmail(""); fetchAll(); }
      else toast.error(data.detail || "Invite failed");
    } catch (e) { toast.error("Something went wrong"); }
    finally { setInviting(false); }
  };

  const handleRemove = async (memberId) => {
    if (!window.confirm("Remove this member from the team?")) return;
    const res = await fetch(`${API}/team/members/${memberId}`, { method: "DELETE", credentials: "include" });
    if (res.ok) { toast.success("Member removed"); fetchAll(); }
    else { const e = await res.json(); toast.error(e.detail || "Failed"); }
  };

  const handleAddPrompt = async () => {
    const res = await fetch(`${API}/team/library`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify(promptForm),
    });
    if (res.ok) { toast.success("Added to shared library"); setShowAddPrompt(false); setPromptForm({ title: "", prompt: "", tool_name: "" }); fetchAll(); }
    else { const e = await res.json(); toast.error(e.detail || "Failed"); }
  };

  const handleDeletePrompt = async (itemId) => {
    const res = await fetch(`${API}/team/library/${itemId}`, { method: "DELETE", credentials: "include" });
    if (res.ok) { toast.success("Deleted"); fetchAll(); }
    else { const e = await res.json(); toast.error(e.detail || "Failed"); }
  };

  const handleExportCSV = async () => {
    try {
      const res = await fetch(`${API}/team/library/export`, { credentials: "include" });
      if (!res.ok) { toast.error("Export failed"); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "team_prompts.csv"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported!");
    } catch (e) { toast.error("Export failed"); }
  };

  const handleGenerateKey = async () => {
    if (team?.api_key_masked && !window.confirm("Generate a new key? The old key will stop working.")) return;
    const res = await fetch(`${API}/team/api-key`, { method: "POST", credentials: "include" });
    if (res.ok) { const data = await res.json(); setNewApiKey(data.api_key); fetchAll(); }
    else { const e = await res.json(); toast.error(e.detail || "Failed"); }
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <Lock className="w-12 h-12 text-purple-400 mx-auto mb-4" />
          <h2 className="font-inter font-bold text-xl mb-2">Team Workspace</h2>
          <p className="text-white/50 text-sm mb-6">Shared prompt library, 5 seats, API routing and CSV export. Available on the Team plan.</p>
          <Link to="/pricing"><Button className="bg-purple-700 text-white hover:bg-purple-600">Upgrade to Team</Button></Link>
        </div>
      </div>
    );
  }

  if (loading) return <div className="min-h-screen bg-dark flex items-center justify-center"><Loader2 className="w-8 h-8 text-purple-400 animate-spin" /></div>;

  const isOwner = team?.is_owner;

  return (
    <div className="min-h-screen bg-dark">
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white/60 hover:text-white"><ChevronLeft className="w-5 h-5" /></Button>
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-purple-700 flex items-center justify-center"><Compass className="w-4 h-4 text-white" /></div>
                <span className="font-inter font-bold text-lg">promptviser</span>
              </Link>
              <Badge className="bg-purple-500/15 text-purple-300 border-purple-500/30 text-[10px]">Team</Badge>
            </div>
            <Link to="/"><Button variant="ghost" size="icon" className="text-white/60 hover:text-white"><Home className="w-5 h-5" /></Button></Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-8" data-testid="team-page">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white mb-6 text-sm"><ArrowLeft className="w-4 h-4" />Dashboard</Link>
        <h1 className="font-inter font-bold text-2xl mb-1">{team?.team?.name || "Team Workspace"}</h1>
        <p className="text-white/40 text-sm mb-8">Seats: {team?.seats_used}/{team?.seats_total} · Shared prompts: {team?.library_count}</p>

        {/* Seat Management */}
        <div className="bg-dark-paper border border-white/10 rounded-xl p-5 mb-6" data-testid="team-members-card">
          <div className="flex items-center gap-2 mb-4"><Users className="w-4 h-4 text-purple-400" /><h2 className="font-semibold">Seat Management</h2></div>
          {isOwner && (
            <div className="flex gap-2 mb-4">
              <Input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="teammate@company.com" className="bg-dark-subtle border-white/10 text-sm" data-testid="invite-email-input" />
              <Button onClick={handleInvite} disabled={inviting} className="bg-purple-700 text-white hover:bg-purple-600 text-xs whitespace-nowrap" data-testid="invite-submit"><UserPlus className="w-3.5 h-3.5 mr-1" />{inviting ? "Adding..." : "Add Member"}</Button>
            </div>
          )}
          <div className="space-y-2">
            {(team?.members || []).map(m => (
              <div key={m.user_id} className="flex items-center justify-between p-2.5 bg-dark rounded-lg border border-white/5" data-testid={`team-member-${m.user_id}`}>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-purple-900/50 flex items-center justify-center text-purple-300 text-xs font-semibold">{m.name?.[0] || "U"}</div>
                  <div><p className="text-sm font-medium">{m.name}</p><p className="text-[10px] text-white/40">{m.email}</p></div>
                  <Badge variant="outline" className="text-[9px] border-white/20 capitalize">{m.team_role || "member"}</Badge>
                </div>
                {isOwner && m.team_role !== "owner" && (
                  <Button variant="ghost" size="sm" onClick={() => handleRemove(m.user_id)} className="text-red-400" data-testid={`remove-member-${m.user_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Shared Library */}
        <div className="bg-dark-paper border border-white/10 rounded-xl p-5 mb-6" data-testid="team-library-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-purple-400" /><h2 className="font-semibold">Shared Prompt Library</h2></div>
            <div className="flex gap-2">
              <Button onClick={() => setShowAddPrompt(true)} size="sm" className="bg-purple-700 text-white hover:bg-purple-600 text-xs" data-testid="add-library-prompt"><Plus className="w-3.5 h-3.5 mr-1" />Add Prompt</Button>
              <Button onClick={handleExportCSV} size="sm" variant="outline" className="border-white/20 text-xs" data-testid="export-csv-btn"><Download className="w-3.5 h-3.5 mr-1" />Export CSV</Button>
            </div>
          </div>
          {library.length === 0 ? (
            <p className="text-white/40 text-sm text-center py-6">No shared prompts yet. Save prompts from search results or add them here.</p>
          ) : (
            <div className="space-y-2">
              {library.map(item => (
                <div key={item.item_id} className="p-3 bg-dark rounded-lg border border-white/5" data-testid={`library-item-${item.item_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold">{item.title}</p>
                        {item.tool_name && <Badge variant="outline" className="text-[9px] border-purple-500/30 text-purple-300">{item.tool_name}</Badge>}
                      </div>
                      <p className="font-mono text-xs text-white/50 line-clamp-2">{item.prompt}</p>
                      <p className="text-[10px] text-white/25 mt-1">by {item.added_by} · {new Date(item.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(item.prompt); toast.success("Copied!"); }}><Copy className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeletePrompt(item.item_id)} className="text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* API Routing + Usage */}
        <div className="bg-dark-paper border border-white/10 rounded-xl p-5" data-testid="team-api-card">
          <div className="flex items-center gap-2 mb-4"><Key className="w-4 h-4 text-purple-400" /><h2 className="font-semibold">API Routing & Usage</h2></div>
          <p className="text-white/50 text-xs mb-3">Route tasks programmatically: <code className="bg-dark px-1.5 py-0.5 rounded text-purple-300">POST /api/v1/route</code> with header <code className="bg-dark px-1.5 py-0.5 rounded text-purple-300">X-API-Key</code> and body <code className="bg-dark px-1.5 py-0.5 rounded text-purple-300">{'{"task": "..."}'}</code></p>
          {newApiKey ? (
            <div className="p-3 bg-neon/5 border border-neon/20 rounded-lg mb-3">
              <p className="text-[10px] text-neon mb-1">Your new API key (copy it now — shown only once):</p>
              <div className="flex items-center gap-2">
                <code className="text-xs text-white/80 break-all" data-testid="new-api-key">{newApiKey}</code>
                <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(newApiKey); toast.success("Key copied!"); }}><Copy className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ) : team?.api_key_masked ? (
            <p className="text-xs text-white/50 mb-3">Current key: <code className="text-purple-300">{team.api_key_masked}</code></p>
          ) : (
            <p className="text-xs text-white/40 mb-3">No API key generated yet.</p>
          )}
          {isOwner && <Button onClick={handleGenerateKey} size="sm" variant="outline" className="border-purple-500/40 text-purple-300 text-xs mb-4" data-testid="generate-api-key">{team?.api_key_masked ? "Regenerate Key" : "Generate API Key"}</Button>}
          {usage && (
            <div className="grid grid-cols-3 gap-3 mt-2">
              <div className="p-3 bg-dark rounded-lg border border-white/5 text-center"><p className="text-[10px] text-white/40 mb-1">Calls this month</p><p className="font-inter font-bold text-lg">{usage.calls_this_month}</p></div>
              <div className="p-3 bg-dark rounded-lg border border-white/5 text-center"><p className="text-[10px] text-white/40 mb-1">Included</p><p className="font-inter font-bold text-lg">{usage.included_calls}</p></div>
              <div className="p-3 bg-dark rounded-lg border border-white/5 text-center"><p className="text-[10px] text-white/40 mb-1">Overage (${usage.overage_rate_usd}/call)</p><p className="font-inter font-bold text-lg">${usage.estimated_overage_usd}</p></div>
            </div>
          )}
        </div>
      </main>

      <Dialog open={showAddPrompt} onOpenChange={setShowAddPrompt}>
        <DialogContent className="bg-dark-paper border-white/10 max-w-lg">
          <DialogHeader><DialogTitle className="text-sm">Add Prompt to Shared Library</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3">
            <div><Label className="text-xs">Title</Label><Input value={promptForm.title} onChange={e => setPromptForm({ ...promptForm, title: e.target.value })} className="bg-dark-subtle border-white/10 text-sm" data-testid="library-title-input" /></div>
            <div><Label className="text-xs">Prompt</Label><Textarea value={promptForm.prompt} onChange={e => setPromptForm({ ...promptForm, prompt: e.target.value })} className="bg-dark-subtle border-white/10 text-sm min-h-[100px]" data-testid="library-prompt-input" /></div>
            <div><Label className="text-xs">AI Tool (optional)</Label><Input value={promptForm.tool_name} onChange={e => setPromptForm({ ...promptForm, tool_name: e.target.value })} className="bg-dark-subtle border-white/10 text-sm" placeholder="e.g., Claude" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPrompt(false)} className="border-white/20 text-xs">Cancel</Button>
            <Button onClick={handleAddPrompt} disabled={!promptForm.title || !promptForm.prompt} className="bg-purple-700 text-white hover:bg-purple-600 text-xs" data-testid="library-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamPage;
